import { ThemeEvents, CartUpdateEvent } from '@theme/events';

const cartCompanionConfig = window.themeCartCompanion || {};
const companionDebug = (...args) => console.warn('[cart-companion]', ...args);

class CartCompanionItemsManager {
  #isMutating = false;
  #variantIds = [];

  constructor() {
    this.#variantIds = (cartCompanionConfig.variantIds || [])
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0);

    if (!cartCompanionConfig.enabled || this.#variantIds.length === 0) return;
    companionDebug('init', { enabled: cartCompanionConfig.enabled, variantIds: this.#variantIds });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  #getSectionIds() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component').forEach((node) => {
      if (node instanceof HTMLElement && node.dataset.sectionId) ids.add(node.dataset.sectionId);
    });
    return Array.from(ids);
  }

  async #addMissingCompanions(missingVariantIds) {
    if (missingVariantIds.length === 0) return null;
    companionDebug('addMissing:start', { missingVariantIds });

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: missingVariantIds.map((id) => ({ id, quantity: 1 })),
        sections: this.#getSectionIds().join(','),
        sections_url: window.location.pathname,
      }),
    });

    const payload = await response.json();
    companionDebug('addMissing:done', { status: response.status, hasSections: Boolean(payload?.sections) });
    return payload;
  }

  async #removeCompanionsFromCart() {
    const updates = {};
    this.#variantIds.forEach((id) => {
      updates[id] = 0;
    });

    companionDebug('removeCompanions:start', { updates });
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        updates,
        sections: this.#getSectionIds().join(','),
        sections_url: window.location.pathname,
      }),
    });

    const payload = await response.json();
    companionDebug('removeCompanions:done', { status: response.status, hasSections: Boolean(payload?.sections) });
    return payload;
  }

  #dispatchCartSectionsUpdate(payload) {
    if (!payload?.sections) return;
    document.dispatchEvent(
      new CartUpdateEvent({}, 'cart-companion-items', {
        source: 'cart-companion-items',
        sections: payload.sections,
      })
    );
  }

  #extractVariantStateFromSections(sections) {
    if (!sections) return null;
    const firstSectionHtml = Object.values(sections).find((value) => typeof value === 'string' && value.length > 0);
    if (!firstSectionHtml) return null;

    const doc = new DOMParser().parseFromString(firstSectionHtml, 'text/html');
    // Only count variants that are actual cart line items — not the bundle "Add" buttons,
    // which also carry data-variant-id but represent products not yet in the cart.
    const variantNodes = doc.querySelectorAll('.cart-items__table-row [data-variant-id]');
    if (variantNodes.length === 0) return null;

    const presentVariantIds = Array.from(variantNodes)
      .map((node) => Number(node.getAttribute('data-variant-id')))
      .filter((id) => Number.isFinite(id) && id > 0);

    const companionSet = new Set(this.#variantIds);
    const companionPresent = presentVariantIds.filter((id) => companionSet.has(id));
    const regularPresent = presentVariantIds.filter((id) => !companionSet.has(id));

    return { companionPresent, regularPresent };
  }

  #handleCartUpdate = async (event) => {
    if (this.#isMutating) return;
    if (event?.detail?.data?.source === 'cart-companion-items') return;
    if (event?.detail?.data?.source === 'cart-gift-countdown') return;
    if (event?.detail?.data?.source === 'cart-items-component') return;
    companionDebug('cartUpdate:event', {
      source: event?.detail?.data?.source,
      sourceId: event?.detail?.sourceId,
    });

    try {
      const companionSet = new Set(this.#variantIds);
      const state = this.#extractVariantStateFromSections(event?.detail?.data?.sections);
      if (!state) return;
      companionDebug('state', state);

      // If only companion items remain in cart, remove them as well.
      if (state.regularPresent.length === 0 && state.companionPresent.length > 0) {
        companionDebug('onlyCompanionsRemain:remove');
        this.#isMutating = true;
        const updatePayload = await this.#removeCompanionsFromCart();
        this.#dispatchCartSectionsUpdate(updatePayload);
        return;
      }

      // Add companions for any real add-to-cart: the product form or the cart bundle block.
      const addSources = ['product-form-component', 'cart-bundle-component'];
      const isAddSource = addSources.includes(event?.detail?.data?.source);
      const addedVariantId = Number(event?.detail?.sourceId);

      if (!isAddSource) return;
      if (companionSet.has(addedVariantId)) return;

      const presentVariantIds = new Set([...state.regularPresent, ...state.companionPresent]);
      const missingVariantIds = this.#variantIds.filter((id) => !presentVariantIds.has(id));

      if (missingVariantIds.length === 0) return;

      this.#isMutating = true;
      const addPayload = await this.#addMissingCompanions(missingVariantIds);
      this.#dispatchCartSectionsUpdate(addPayload);
    } catch (error) {
      console.error('Cart companion sync failed:', error);
    } finally {
      this.#isMutating = false;
    }
  };
}

new CartCompanionItemsManager();
